import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Loader2, UserPlus } from 'lucide-react'
import { Keyboard } from '@capacitor/keyboard'
import { Capacitor } from '@capacitor/core'
import { doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { nanoid } from 'nanoid'
import { getPlatform, isNative } from '@/lib/platform'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { db } from '@/lib/firebase'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import {
  MAX_MONTHLY_ARTIST_INVITES,
  daysUntilNextCalendarMonthLocal,
  fetchArtistInvitesThisMonth,
  msUntilNextCalendarMonthLocal,
  type ArtistInviteRow,
} from '@/lib/artistInvites'

interface InviteArtistDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type SubmitPhase = 'idle' | 'submitting' | 'success'

/** Short label for sideways tag chips under the Invite button */
function truncateInviteTag(text: string, maxLen: number): string {
  const s = text.trim().replace(/\s+/g, ' ')
  if (s.length <= maxLen) return s
  return `${s.slice(0, maxLen - 1)}…`
}

export function InviteArtistDialog({ open, onOpenChange }: InviteArtistDialogProps) {
  const { i18n } = useTranslation()
  /** Invite dialog copy stays English-only (see locale files). */
  const inviteCopy = useMemo(() => i18n.getFixedT('en'), [i18n])
  const { user } = useAuthState()
  const { artistId } = useUserData()
  const [mounted, setMounted] = useState(false)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const [draft, setDraft] = useState('')
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('idle')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [invites, setInvites] = useState<ArtistInviteRow[]>([])
  const [invitesLoading, setInvitesLoading] = useState(false)
  const successResetRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const monthResetRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const loadInvites = useCallback(async () => {
    if (!artistId) return
    setInvitesLoading(true)
    try {
      const rows = await fetchArtistInvitesThisMonth(artistId)
      setInvites(rows)
    } catch (e) {
      console.error('[InviteArtistDialog] Failed to load invites:', e)
    } finally {
      setInvitesLoading(false)
    }
  }, [artistId])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!open) {
      setDraft('')
      setSubmitPhase('idle')
      setValidationError(null)
      setSaveError(null)
      if (successResetRef.current) {
        clearTimeout(successResetRef.current)
        successResetRef.current = null
      }
      if (monthResetRef.current) {
        clearTimeout(monthResetRef.current)
        monthResetRef.current = null
      }
      return
    }

    void loadInvites()
  }, [open, loadInvites])

  useEffect(() => {
    if (!open) return

    const scheduleMonthReset = () => {
      if (monthResetRef.current) clearTimeout(monthResetRef.current)
      monthResetRef.current = setTimeout(() => {
        monthResetRef.current = null
        void loadInvites()
        scheduleMonthReset()
      }, msUntilNextCalendarMonthLocal() + 50)
    }

    scheduleMonthReset()

    return () => {
      if (monthResetRef.current) {
        clearTimeout(monthResetRef.current)
        monthResetRef.current = null
      }
    }
  }, [open, loadInvites])

  useEffect(() => {
    return () => {
      if (successResetRef.current) {
        clearTimeout(successResetRef.current)
      }
      if (monthResetRef.current) {
        clearTimeout(monthResetRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const platform = getPlatform()
    if (platform !== 'ios') return

    let showListener: { remove: () => Promise<void> } | undefined
    let hideListener: { remove: () => Promise<void> } | undefined

    const setup = async () => {
      showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
        setKeyboardHeight(info.keyboardHeight)
      })
      hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0)
      })
    }

    void setup()

    return () => {
      void showListener?.remove()
      void hideListener?.remove()
    }
  }, [])

  const dismissKeyboard = useCallback(() => {
    inputRef.current?.blur()
    if (Capacitor.isNativePlatform()) {
      void Keyboard.hide().catch(() => {})
    }
  }, [])

  const getModalTop = () => {
    if (keyboardHeight > 0) {
      return `calc((100dvh - ${keyboardHeight}px) / 2)`
    }
    return isNative() ? 'calc(100dvh / 2)' : '50%'
  }

  const usedThisMonth = invites.length
  const remainingInvites = Math.max(0, MAX_MONTHLY_ARTIST_INVITES - usedThisMonth)
  const quotaExhausted = remainingInvites <= 0
  const resetDays = daysUntilNextCalendarMonthLocal()

  const remainingLabel =
    remainingInvites === 0
      ? inviteCopy('inviteArtist.remainingNone')
      : remainingInvites === 1
        ? inviteCopy('inviteArtist.remainingOne')
        : inviteCopy('inviteArtist.remainingMany', { count: remainingInvites })

  const resetLabel =
    resetDays <= 1 ? inviteCopy('inviteArtist.resetTomorrow') : inviteCopy('inviteArtist.resetInDays', { days: resetDays })

  const handleOverlayPointerDown = () => {
    if (submitPhase !== 'idle') return
    dismissKeyboard()
    onOpenChange(false)
  }

  const handleSubmitInvite = async () => {
    setSaveError(null)
    setValidationError(null)

    if (submitPhase !== 'idle') return

    const text = draft.trim()
    if (!text) {
      setValidationError(inviteCopy('inviteArtist.validationRequired'))
      return
    }
    if (!user || !artistId) {
      setSaveError(inviteCopy('inviteArtist.errorNotArtist'))
      return
    }
    if (invitesLoading) return
    if (quotaExhausted) {
      setValidationError(inviteCopy('inviteArtist.quotaExhaustedShort'))
      return
    }

    setSubmitPhase('submitting')

    try {
      const inviteDocId = `${artistId}_${nanoid(12)}`
      await setDoc(doc(db, 'artistInvites', inviteDocId), {
        artistId,
        inviteInput: text,
        createdAt: serverTimestamp(),
        onboarded: false,
      })
      dismissKeyboard()
      const rows = await fetchArtistInvitesThisMonth(artistId)
      setInvites(rows)
      setDraft('')

      setSubmitPhase('success')
      if (successResetRef.current) clearTimeout(successResetRef.current)
      successResetRef.current = setTimeout(() => {
        setSubmitPhase('idle')
        successResetRef.current = null
      }, 1800)
    } catch (err) {
      console.error('[InviteArtistDialog] Failed to save invite:', err)
      setSaveError(inviteCopy('inviteArtist.errorSaving'))
      setSubmitPhase('idle')
    }
  }

  if (!mounted || !open) return null

  const inputDisabled = submitPhase !== 'idle' || quotaExhausted || invitesLoading
  const buttonDisabled =
    submitPhase === 'submitting' || submitPhase === 'success' || quotaExhausted || invitesLoading

  return (
    <>
      <div
        role="presentation"
        className="fixed inset-0 z-[55] bg-black/50 animate-in fade-in-0"
        onClick={handleOverlayPointerDown}
      />

      <div
        className="fixed left-0 right-0 z-[60] flex max-h-[90dvh] items-center justify-center pointer-events-none px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-artist-title"
        aria-describedby="invite-artist-description invite-artist-quota"
        style={{
          top: getModalTop(),
          transform: 'translateY(-50%)',
          transition: 'top 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        <div className="relative w-full max-w-sm overflow-y-auto rounded-[var(--card-radius)] bg-background shadow-lg animate-in zoom-in-95 pointer-events-auto">
          <div className="space-y-4 p-6">
            <div className="space-y-1.5 pr-2">
              <h2 id="invite-artist-title" className="text-xl font-semibold tracking-tight text-foreground">
                {inviteCopy('inviteArtist.title')}
              </h2>
              <p
                id="invite-artist-quota"
                className="max-w-full text-sm leading-snug text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis"
              >
                {invitesLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin opacity-60" aria-hidden />
                    {inviteCopy('inviteArtist.quotaLoading')}
                  </span>
                ) : (
                  <>
                    {remainingLabel}
                    <span className="text-muted-foreground/80"> · </span>
                    {resetLabel}
                  </>
                )}
              </p>
            </div>

            <div id="invite-artist-description" className="space-y-2">
              <p className="text-sm leading-relaxed text-foreground">
                {inviteCopy('inviteArtist.descriptionParagraph1')}
              </p>
              <p className="text-sm leading-relaxed text-foreground">
                {inviteCopy('inviteArtist.descriptionParagraph2')}
              </p>
              <p className="text-sm leading-relaxed text-foreground">
                {inviteCopy('inviteArtist.descriptionParagraph4')}
              </p>
            </div>

            <Input
              ref={inputRef}
              value={draft}
              disabled={inputDisabled}
              onChange={(e) => {
                setDraft(e.target.value)
                setValidationError(null)
                setSaveError(null)
              }}
              placeholder={inviteCopy('inviteArtist.inputPlaceholder')}
              enterKeyHint="done"
              className={cn(
                'h-12 rounded-xl text-base transition-[opacity,background-color,border-color,color]',
                quotaExhausted
                  ? 'cursor-not-allowed border-border/30 bg-muted/50 text-muted-foreground placeholder:text-muted-foreground/70 disabled:pointer-events-none disabled:opacity-[0.42]'
                  : 'border-border bg-background disabled:opacity-50',
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (submitPhase !== 'idle') return
                  if (draft.trim()) {
                    void handleSubmitInvite()
                  } else {
                    dismissKeyboard()
                  }
                }
              }}
            />

            {validationError ? (
              <p className="text-sm text-destructive" role="alert">
                {validationError}
              </p>
            ) : null}
            {saveError ? (
              <p className="text-sm text-destructive" role="alert">
                {saveError}
              </p>
            ) : null}

            <Button
              type="button"
              variant="default"
              className={cn(
                'h-12 w-full gap-2 rounded-xl text-base font-medium transition-colors duration-300',
                (quotaExhausted || invitesLoading) && 'disabled:opacity-[0.38]',
                submitPhase === 'success' &&
                  'border-0 bg-emerald-600 text-white hover:bg-emerald-600 focus-visible:ring-emerald-500 disabled:opacity-100',
              )}
              disabled={buttonDisabled}
              onClick={() => void handleSubmitInvite()}
            >
              {submitPhase === 'submitting' ? (
                <>
                  <Loader2 className="h-5 w-5 shrink-0 animate-spin" aria-hidden />
                  <span>{inviteCopy('inviteArtist.savingInvite')}</span>
                </>
              ) : submitPhase === 'success' ? (
                <>
                  <Check className="h-5 w-5 shrink-0" strokeWidth={2.5} aria-hidden />
                  <span>{inviteCopy('inviteArtist.doneCelebration')}</span>
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 shrink-0" aria-hidden />
                  <span>{inviteCopy('inviteArtist.cta')}</span>
                </>
              )}
            </Button>

            {invites.length > 0 && !invitesLoading ? (
              <div id="invite-artist-sent" className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground">{inviteCopy('inviteArtist.invitesHeading')}</p>
                <ul
                  role="list"
                  aria-label={inviteCopy('inviteArtist.invitesListAria')}
                  className="scrollbar-hide flex touch-pan-x gap-2 overflow-x-auto overflow-y-hidden pb-1 pl-px"
                >
                  {invites.map((inv) => (
                    <li key={inv.id} className="shrink-0" role="listitem">
                      <span
                        title={inv.inviteInput.trim()}
                        className={cn(
                          'inline-block max-w-[12rem] truncate rounded-full border px-3 py-1.5 text-xs font-medium leading-tight',
                          inv.onboarded
                            ? 'border-transparent bg-emerald-600 text-white'
                            : 'border-border bg-background text-foreground',
                        )}
                      >
                        {truncateInviteTag(inv.inviteInput, 44)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}
